/**
 * gameStore — Zustand store reflecting server-authoritative GameState.
 *
 * The server is the source of truth. This store holds the latest
 * `GameState` snapshot received from a `gameState` ServerMsg and the
 * connection-scoped phase machine the UI uses to gate rendering.
 *
 * Mirrors `lobbyStore.ts` in shape and naming, except it subscribes
 * exclusively to `GameStateMsg` frames. The lobby and game stores share
 * a single `WsClient` instance routed through `useLobbyStore.connect()`
 * — the lobby store is always brought up first; this store hooks into
 * the same client by listening for the lobby store's connection
 * lifecycle and registering its own ServerMsg handler.
 *
 * All "actions" are dispatchers — they call `client.send(...)` rather
 * than mutating state locally. Local mutations would race with server
 * broadcasts and cause flicker; instead the UI waits for the
 * authoritative `gameState` reply and re-renders from that.
 *
 * Stale-frame protection: incoming `gameState` messages with `seq`
 * lower than the last applied are dropped. The reducer increments
 * `seq` on every successful application, so this is a safe monotonic
 * filter against out-of-order delivery on reconnect.
 */

import { create } from 'zustand';
import {
  type AttackState,
  type ClientSafeQuestion,
  type DashboardState,
  type DocCardId,
  type GameAction,
  type GameEvent,
  type GameState,
  type LearningAnswer,
  type Player,
  type PlayerId,
  type QuestionId,
  type ServerMsg,
  type TerritoryRuntimeState,
  type TurnPhase,
} from '@qd/protocol';

import { useLobbyStore } from './lobbyStore';

/** Lifecycle of the in-match WebSocket from this store's perspective. */
export type GamePhase = 'idle' | 'syncing' | 'live' | 'ended';

interface GameStoreState {
  // ─── Connection identity (mirrors lobby store, kept in sync) ───
  /** Latest authoritative GameState; null while syncing or in lobby. */
  gameState: GameState | null;
  /** Most recently applied `seq`; used to drop stale frames. */
  lastSeq: number;
  /** Coarse phase machine for the gameplay overlay. */
  phase: GamePhase;

  // ─── Lifecycle ───
  /**
   * Wire up the server-message subscription. Idempotent — calling this
   * twice in a row is a no-op. The lobby store owns the underlying
   * WebSocket; we just attach a listener.
   */
  attach(): void;
  /** Tear down the subscription and clear gameplay state. */
  detach(): void;

  // ─── Action dispatchers (each sends a real GameActionMsg) ───
  selectSource(territoryId: string): void;
  selectTarget(territoryId: string): void;
  commitAttack(cubes: 1 | 2 | 3): void;
  answerQuestion(choiceIndex: number | boolean): void;
  /**
   * Send END_COMBAT. Two callers in the buzzer-race redesign:
   *   1. PhaseControls "Cancel" button at `targetSelected` — bails the
   *      attacker out of a queued attack before they commit cubes.
   *   2. ChalkQuizModal's `combatResolved` auto-dismiss — fires after a
   *      ~2.5s reveal so the modal advances to `idle` without a
   *      Continue button (CONTINUE_ATTACK / chain combat is removed).
   * The reducer's END_COMBAT branch already accepts both `targetSelected`
   * and `combatResolved` as valid source states.
   */
  endCombat(): void;
  moveCubesAfterConquest(count: number): void;
  playDocumentationCard(cardId: DocCardId): void;
  deployCubes(territoryId: string, count: number): void;
  fortify(fromId: string, toId: string, count: number): void;
  endPhase(): void;
  endTurn(): void;
  /**
   * Log a non-attacker (or attacker, idempotent) learning answer.
   * Combat outcome is unchanged — only `answerQuestion` advances the
   * combat state. Server records correctness server-side and broadcasts
   * via `learningAnswers`.
   */
  submitLearningAnswer(
    playerId: PlayerId,
    questionId: QuestionId,
    choiceIndex: number | boolean,
  ): void;
  /**
   * Force-end the current player's turn with a missed in-flight combat.
   * Fired by the attacker's modal when its local timer expires; server
   * validates that the configured session timer has actually elapsed.
   */
  turnTimeout(playerId: PlayerId): void;
}

/** Top-level Zustand instance. The store name matches the convention
 *  Zustand devtools picks up (`'qd-game'`). We don't wrap with the
 *  devtools middleware here to avoid a hard dep — Zustand's React
 *  DevTools work without it for create(). */
export const useGameStore = create<GameStoreState>((set, get) => {
  // Holds the most recent `attach` cleanup so we can be re-entered.
  let detachHandlers: (() => void) | null = null;

  function dispatch(action: GameAction): void {
    const client = useLobbyStore.getState().client;
    if (!client) {
      // eslint-disable-next-line no-console
      console.warn('[gameStore] dropped action — no socket', action.type);
      return;
    }
    client.send({ type: 'gameAction', action });
  }

  return {
    gameState: null,
    lastSeq: -1,
    phase: 'idle',

    attach() {
      if (detachHandlers) return;

      // Subscribe to the lobby store so we can latch onto whatever
      // WsClient instance it's currently using. When the lobby store
      // (re)connects we wire a fresh ServerMsg handler; when it
      // disconnects we drop our handler.
      let offMsg: (() => void) | null = null;
      let lastClient: ReturnType<typeof useLobbyStore.getState>['client'] = null;

      const wireClient = (client: ReturnType<typeof useLobbyStore.getState>['client']) => {
        if (offMsg) {
          offMsg();
          offMsg = null;
        }
        lastClient = client;
        if (!client) return;
        offMsg = client.onMessage((msg: ServerMsg) => {
          handleServerMessage(set, get, msg);
        });
      };

      // Wire to the current client (if any) immediately…
      wireClient(useLobbyStore.getState().client);
      // …and re-wire whenever the lobby store swaps clients.
      const unsubscribe = useLobbyStore.subscribe((s) => {
        if (s.client !== lastClient) {
          wireClient(s.client);
        }
      });

      detachHandlers = () => {
        if (offMsg) offMsg();
        offMsg = null;
        unsubscribe();
        detachHandlers = null;
      };

      set({ phase: 'syncing' });
    },

    detach() {
      if (detachHandlers) {
        detachHandlers();
        detachHandlers = null;
      }
      set({ gameState: null, lastSeq: -1, phase: 'idle' });
    },

    selectSource(territoryId) {
      dispatch({ type: 'SELECT_SOURCE', territoryId });
    },
    selectTarget(territoryId) {
      dispatch({ type: 'SELECT_TARGET', territoryId });
    },
    commitAttack(cubes) {
      dispatch({ type: 'COMMIT_ATTACK', cubes });
    },
    answerQuestion(choiceIndex) {
      // Buzzer-race semantics: reducer accepts ANSWER_QUESTION from
      // EITHER the attacker OR the defender (the territory's controller
      // at combatActive). First correct answer resolves combat; a wrong
      // answer locks the submitter out of re-submission and applies a
      // -1 cube penalty to their side. The wire shape is unchanged —
      // server resolves identity from the socket's authenticated player.
      dispatch({ type: 'ANSWER_QUESTION', choiceIndex });
    },
    endCombat() {
      dispatch({ type: 'END_COMBAT' });
    },
    moveCubesAfterConquest(count) {
      dispatch({ type: 'MOVE_CUBES_AFTER_CONQUEST', count });
    },
    playDocumentationCard(cardId) {
      dispatch({ type: 'PLAY_DOCUMENTATION_CARD', cardId });
    },
    deployCubes(territoryId, count) {
      dispatch({ type: 'DEPLOY_CUBES', territoryId, count });
    },
    fortify(fromId, toId, count) {
      dispatch({ type: 'FORTIFY', fromId, toId, count });
    },
    endPhase() {
      dispatch({ type: 'END_PHASE' });
    },
    endTurn() {
      dispatch({ type: 'END_TURN' });
    },
    submitLearningAnswer(playerId, questionId, choiceIndex) {
      dispatch({
        type: 'SUBMIT_LEARNING_ANSWER',
        playerId,
        questionId,
        choiceIndex,
      });
    },
    turnTimeout(playerId) {
      dispatch({ type: 'TURN_TIMEOUT', playerId });
    },
  };
});

// -----------------------------------------------------------------------------
// Reducer for incoming server messages
// -----------------------------------------------------------------------------

function handleServerMessage(
  set: (
    partial:
      | Partial<GameStoreState>
      | ((prev: GameStoreState) => Partial<GameStoreState>),
  ) => void,
  get: () => GameStoreState,
  msg: ServerMsg,
): void {
  if (msg.type !== 'gameState') return;
  const incoming = msg.state;
  if (incoming.seq <= get().lastSeq) return;
  const phase: GamePhase = incoming.outerPhase === 'ended' ? 'ended' : 'live';
  set({ gameState: incoming, lastSeq: incoming.seq, phase });
}

// -----------------------------------------------------------------------------
// Selectors — typed convenience hooks for components
// -----------------------------------------------------------------------------

const EMPTY_EVENTS: readonly GameEvent[] = [];
const IDLE_ATTACK: AttackState = { kind: 'idle' };

export function useGameState(): GameState | null {
  return useGameStore((s) => s.gameState);
}

export function useGamePhase(): GamePhase {
  return useGameStore((s) => s.phase);
}

export function useTurnPhase(): TurnPhase | null {
  return useGameStore((s) => s.gameState?.phase ?? null);
}

export function useCurrentPlayer(): Player | null {
  return useGameStore((s) => {
    const gs = s.gameState;
    if (!gs) return null;
    const id = gs.playerOrder[gs.currentPlayerIdx];
    if (!id) return null;
    return gs.players.find((p) => p.id === id) ?? null;
  });
}

export function useSelfPlayer(myId: PlayerId | null): Player | null {
  return useGameStore((s) => {
    if (!s.gameState || !myId) return null;
    return s.gameState.players.find((p) => p.id === myId) ?? null;
  });
}

export function useSelfDashboard(myId: PlayerId | null): DashboardState | null {
  return useGameStore((s) => {
    if (!s.gameState || !myId) return null;
    const me = s.gameState.players.find((p) => p.id === myId);
    return me?.dashboard ?? null;
  });
}

export function useTerritory(id: string | null): TerritoryRuntimeState | null {
  return useGameStore((s) => {
    if (!s.gameState || !id) return null;
    return s.gameState.territories[id] ?? null;
  });
}

export function useAttackState(): AttackState {
  return useGameStore((s) => s.gameState?.attackState ?? IDLE_ATTACK);
}

export function useActiveQuestion(): ClientSafeQuestion | null {
  return useGameStore((s) => {
    const a = s.gameState?.attackState;
    if (!a || a.kind !== 'combatActive') return null;
    return a.question;
  });
}

export function useEventLog(): readonly GameEvent[] {
  return useGameStore((s) => s.gameState?.eventLog ?? EMPTY_EVENTS);
}

/**
 * Look up a single (questionId, playerId) learning-answer entry. Returns
 * null when the player hasn't submitted yet for that question. The
 * modal uses this to show per-player feedback at combatResolved without
 * having to walk the entire learningAnswers map.
 */
export function useLearningAnswer(
  questionId: QuestionId | null,
  playerId: PlayerId | null,
): LearningAnswer | null {
  return useGameStore((s) => {
    if (!s.gameState || !questionId || !playerId) return null;
    return s.gameState.learningAnswers[questionId]?.[playerId] ?? null;
  });
}

/** Convenience: check whether `myId` is the active player. */
export function useIsMyTurn(myId: PlayerId | null): boolean {
  return useGameStore((s) => {
    if (!s.gameState || !myId) return false;
    const id = s.gameState.playerOrder[s.gameState.currentPlayerIdx];
    return id === myId;
  });
}
